/**
 * /agent/chats — list page (Stage 1).
 *
 * Mirrors ChatsListPage but hits the /v2/agent/chats API.
 * "New chat" navigates to /agent/new-chat (separate page
 * per the user's request, vs the existing chats-list which creates inline).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, MessageSquare, Plus, Trash2 } from 'lucide-react';
import {
    agentApi,
    type AgentChat,
} from '@/lib/agent.api';
import { getApiErrorMessage } from '@/lib/http';
import { agentSurfaceForPath } from '@/app/agent/surface';

function formatRelative(updatedAt: string | null) {
    if (!updatedAt) return '';
    const then = new Date(updatedAt).getTime();
    const diff = Date.now() - then;
    if (Number.isNaN(diff) || diff < 0) return '';

    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(updatedAt).toLocaleDateString();
}

export function AgentChatsListPage() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const surface = useMemo(() => agentSurfaceForPath(pathname), [pathname]);
    const [chats, setChats] = useState<AgentChat[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);

    const fetchChats = useCallback(async () => {
        setError(null);
        try {
            const { results } = await agentApi.list({
                agentType: surface.agentType,
                includeTotal: false,
            });
            setChats(results);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not load searches.'));
        } finally {
            setLoading(false);
        }
    }, [surface.agentType]);

    useEffect(() => {
        void fetchChats();
    }, [fetchChats]);

    async function handleDelete(chatId: string) {
        setPendingDelete(chatId);
        try {
            await agentApi.archive(chatId);
            setChats((current) => current.filter((c) => c._id !== chatId));
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not archive chat.'));
        } finally {
            setPendingDelete(null);
        }
    }

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--surface-primary)',
                fontFamily: 'var(--font-sans)',
                color: 'var(--text-primary)',
            }}
        >
            <div
                style={{
                    width: '100%',
                    maxWidth: 1120,
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
            <header
                style={{
                    padding: '16px 40px 24px',
                    borderBottom: '1px solid var(--border-muted)',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 16,
                }}
            >
                <div>
                    <h1
                        style={{
                            fontFamily: 'var(--font-serif)',
                            fontSize: 32,
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            margin: 0,
                        }}
                    >
                        {surface.listTitle}
                    </h1>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                        {surface.listSubtitle}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => navigate(surface.newPath)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        background: 'var(--accent)',
                        color: '#FFFFFF',
                        border: 'none',
                        borderRadius: 9999,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: 'pointer',
                    }}
                >
                    <Plus size={14} strokeWidth={2} />
                    New search
                </button>
            </header>

            {error ? (
                <div
                    style={{
                        margin: '16px 40px 0',
                        padding: '10px 14px',
                        background: 'var(--bg-danger)',
                        color: 'var(--fg-danger)',
                        borderRadius: 8,
                        fontSize: 13,
                    }}
                >
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-tertiary)',
                    }}
                >
                    <Loader2 size={18} strokeWidth={1.75} className="animate-spin" />
                </div>
            ) : chats.length === 0 ? (
                <EmptyState />
            ) : (
                <div style={{ padding: '16px 40px 40px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {chats.map((chat) => (
                        <ChatRow
                            key={chat._id}
                            chat={chat}
                            isDeleting={pendingDelete === chat._id}
                            onOpen={() => navigate(`${surface.detailBase}/${chat._id}`)}
                            onDelete={() => handleDelete(chat._id)}
                            relative={formatRelative(chat.lastMessageAt || chat.updatedAt)}
                        />
                    ))}
                </div>
            )}
            </div>
        </div>
    );
}

function EmptyState() {
    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 40,
            }}
        >
            <div style={{ textAlign: 'center', maxWidth: 420, color: 'var(--text-tertiary)' }}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: 'var(--surface-tertiary)',
                        color: 'var(--text-secondary)',
                        marginBottom: 16,
                    }}
                >
                    <MessageSquare size={20} strokeWidth={1.6} />
                </span>
                <p style={{ margin: 0, fontSize: 15, color: 'var(--text-primary)', fontWeight: 500 }}>
                    No searches yet
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    Start a new search to begin.
                </p>
            </div>
        </div>
    );
}

function ChatRow({
    chat,
    isDeleting,
    onOpen,
    onDelete,
    relative,
}: {
    chat: AgentChat;
    isDeleting: boolean;
    onOpen: () => void;
    onDelete: () => void;
    relative: string;
}) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                border: '1px solid var(--border-muted)',
                borderRadius: 12,
                background: 'var(--surface-primary)',
                transition: 'border-color 120ms',
            }}
        >
            <button
                type="button"
                onClick={onOpen}
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: 'inherit',
                    minWidth: 0,
                }}
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'var(--surface-tertiary)',
                        color: 'var(--text-secondary)',
                        flexShrink: 0,
                    }}
                >
                    <MessageSquare size={15} strokeWidth={1.7} />
                </span>
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <strong
                        style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {chat.title || 'Untitled chat'}
                    </strong>
                    <span
                        style={{
                            fontSize: 12,
                            color: 'var(--text-tertiary)',
                            marginTop: 2,
                        }}
                    >
                        {relative}
                    </span>
                </span>
            </button>
            <button
                type="button"
                onClick={onDelete}
                disabled={isDeleting}
                aria-label={`Archive ${chat.title || 'chat'}`}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-tertiary)',
                    cursor: isDeleting ? 'wait' : 'pointer',
                    opacity: isDeleting ? 0.5 : 1,
                    flexShrink: 0,
                }}
            >
                {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} strokeWidth={1.7} />}
            </button>
        </div>
    );
}
